import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  Input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl} from '@angular/forms';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldControl, MatSuffix} from '@angular/material/form-field';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {FlexModule} from '@angular/flex-layout';
import {Subject} from 'rxjs';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {FocusMonitor} from '@angular/cdk/a11y';
import {ApplicationAssessmentStatus, Retailer, TenantType} from '@features/leads/retailers/retailers.model';
import {
  RetailerLookupDialogComponent,
  RetailerLookupDialogData,
} from './retailer-lookup-dialog/retailer-lookup-dialog.component';
import {RetailerCacheService} from './retailer-cache.service';
import {RetailerLookupComponentOptions} from "./retailer-lookup.model";

@Component({
  selector: 'app-retailer-lookup',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, FlexModule, MatSuffix],
  templateUrl: './retailer-lookup.component.html',
  styleUrls: ['./retailer-lookup.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RetailerLookupComponent),
      multi: true,
    },
    {
      provide: MatFormFieldControl,
      useExisting: RetailerLookupComponent,
    },
  ],
})
export class RetailerLookupComponent implements OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<number | null> {
  static nextId = 0;

  @Input() offeringUuid!: string;
  options = input<Partial<RetailerLookupComponentOptions>>();

  private _options = computed<RetailerLookupComponentOptions>(() => {
    const nullsafeOptions: RetailerLookupComponentOptions = {
      allowedRetailerTenantTypes: Object.values(TenantType),
      allowedCurationStatus: Object.values(ApplicationAssessmentStatus),
      allowedRetailerFilterPredicate: (f: Retailer) => true
    };

    const inputOptions = this.options();

    if (!inputOptions) {
      return nullsafeOptions;
    }

    if (inputOptions.allowedRetailerTenantTypes !== undefined && inputOptions.allowedRetailerTenantTypes !== null) {
      nullsafeOptions.allowedRetailerTenantTypes = inputOptions.allowedRetailerTenantTypes;
    }

    if (inputOptions.allowedCurationStatus !== undefined && inputOptions.allowedCurationStatus !== null) {
      nullsafeOptions.allowedCurationStatus = inputOptions.allowedCurationStatus;
    }

    if (inputOptions.allowedRetailerFilterPredicate !== undefined && inputOptions.allowedRetailerFilterPredicate !== null) {
      nullsafeOptions.allowedRetailerFilterPredicate = inputOptions.allowedRetailerFilterPredicate;
    }

    return nullsafeOptions;
  })

  @Input()
  get placeholder(): string { return this._placeholder; }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder = 'Select Retailer';

  @Input()
  get required(): boolean { return this._required; }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get value(): number | null { return this._valueSignal(); }
  set value(val: number | null) {
    this._valueSignal.set(val);
    this.stateChanges.next();
  }

  readonly retailerSelected = output<Retailer | null>();

  readonly stateChanges = new Subject<void>();
  readonly id = `retailer-lookup-${RetailerLookupComponent.nextId++}`;
  focused = false;
  touched = false;
  controlType = 'retailer-lookup';
  autofilled?: boolean;
  ngControl: NgControl | null = null;

  private readonly _valueSignal = signal<number | null>(null);

  protected readonly selectedRetailer = computed(() => {
    const id = this._valueSignal();
    if (id === null) return null;
    return this.retailerCacheService.getRetailersSignal(this.offeringUuid)()
      .find(r => r.id === id) ?? null;
  });

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  private readonly dialog = inject(MatDialog);
  private readonly retailerCacheService = inject(RetailerCacheService);
  private lookupDialogRef: MatDialogRef<RetailerLookupDialogComponent, Retailer | null> | null = null;
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);

  get empty(): boolean { return this._valueSignal() === null; }
  get shouldLabelFloat(): boolean { return this.focused || !this.empty; }
  get errorState(): boolean { return this.touched && this.empty && this._required; }

  private readonly _retailerEffect = effect(() => {
    const retailer = this.selectedRetailer();
    if (retailer !== null) {
      this.retailerSelected.emit(retailer);
      this.stateChanges.next();
    }
  });

  ngOnInit(): void {
    setTimeout(() => {
      this.ngControl = this.injector.get(NgControl, null);
    });

    this.retailerCacheService.initialize(this.offeringUuid);
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  onFocusIn(): void {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
    this.focused = false;
    this.stateChanges.next();
  }

  setDescribedByIds(ids: string[]): void {
    const el = this.elementRef.nativeElement.querySelector('.retailer-lookup-container');
    el?.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick(): void {
    if (!this._disabled) {
      this.openLookupDialog();
    }
  }

  protected openLookupDialog(): void {
    if (this._disabled || this.lookupDialogRef) return;
    const data: RetailerLookupDialogData = {
      offeringUuid: this.offeringUuid,
      ... this._options()
    };
    this.lookupDialogRef = this.dialog.open(RetailerLookupDialogComponent, {
      panelClass: 'dialog-large-responsive',
      data,
    });
    this.lookupDialogRef.afterClosed().subscribe((retailer: Retailer | null | undefined) => {
      this.lookupDialogRef = null;
      if (retailer != null) {
        this._valueSignal.set(retailer.id ?? null);
        this.onChange(this._valueSignal());
        this.stateChanges.next();
      }
      if (!this.touched) {
        this.onTouched();
        this.touched = true;
      }
      this.focused = false;
      this.stateChanges.next();
    });
  }

  writeValue(id: number | null): void {
    this._valueSignal.set(id);
    this.stateChanges.next();
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
    this.stateChanges.next();
  }
}
